import React from "react";
import { useParams } from "react-router-dom";
import { ServiceContent } from "../types";
import { FaCheckCircle, FaRegNewspaper, FaShieldAlt, FaUserCheck } from "react-icons/fa";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { MdOutlineScience } from "react-icons/md";
import { RiSpeedLine } from "react-icons/ri";
import { ArrowRight } from "lucide-react";
import { useBookAssessment } from "../hooks/useBookAssessment";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { buildServiceSchema, type Crumb } from "../utils/structuredData";
import NotFound from "./NotFound";


const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const bookAssessment = useBookAssessment();

  // --- Service Content Map ---
  const contentMap: Record<string, ServiceContent> = {
    "virtual-archives": {
      title: "Virtual Archives",
      tagline: "Your AI-Powered Document Intelligence Engine.",
      description: "A pre-built private intelligence engine that turns your static documents into a secure, queryable knowledge base. Instead of manually searching through file shares, scanned archives, and case files, your team asks a question in plain language and gets an answer sourced directly from the underlying documents, with a citation back to where it came from. Your data stays private and under your control, with unmetered access and no usage-based charges. Deploy it where it fits your environment: private cloud or on-prem, your call. Because it's a pre-built solution rather than a custom build from scratch, it can be stood up faster than a bespoke integration project while still fitting inside your existing security boundary.",
      metaDescription: "A private AI engine that turns your documents into a secure, searchable knowledge base — deployed in your cloud or on-prem, with no usage-based charges.",
      features: [
        { name: "Maximum Privacy", description: "Runs inside your own secure environment so sensitive data stays under your control — private cloud or on-prem, whichever fits.", icon: <FaShieldAlt className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Intelligent Search", description: "Ask complex questions in natural language and get precise answers sourced directly from your files.", icon: <FaMagnifyingGlass className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Document Citation", description: "The AI intelligently indexes and references your documents, providing you links to relevant material, saving you time.", icon: <FaRegNewspaper className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Data Synthesis", description: "Connects information across multiple documents to provide comprehensive summaries and insights.", icon: <MdOutlineScience className="w-8 h-8 mb-4 text-primary" /> },
      ],
      useCases: [
          { title: "Legal Professionals", description: "Instantly cross-reference thousands of case files, contracts, and legal precedents to build stronger arguments." },
          { title: "Medical Researchers", description: "Analyze decades of research papers and patient records securely to identify trends and correlations." },
          { title: "Financial Auditing", description: "Securely process and query vast amounts of financial statements and reports to detect anomalies." },
      ],
      demo: { type: 'video', src: '/assets/demos/virtualArchives.mp4', alt: 'A demo video showing the Virtual Archives in action.' },
      cta: { 
        headline: "Ready to Unlock Your Data's Potential?", 
        buttonText: "Book a Free Automation Assessment",
        description: "We'll assess your document workflows and show where a secure private-intelligence solution can save the most time and risk."
      },
      caseStudy: {
        href: "/assets/caseStudies/virtualArchives.pdf",
        filename: "Virtual-Archives-Case-Study.pdf",
        label: "Download Case Study"
      }
    },
    "formulaic": {
      title: "Formulaic",
      tagline: "Intelligent Form Completion, Simplified.",
      description: "Eliminate costly errors and tedious data entry. Formulaic acts as an intelligent layer over your forms, actively guiding users and cross-validating information against your existing documents to ensure every submission is perfect. Instead of a static PDF or web form that accepts whatever a user types, Formulaic checks entries against your own records and external systems as they're entered, catching mismatches before a form is ever submitted rather than after it's already caused a delay or a billing error. It plugs into the intake process you already run — no need to migrate to a new forms platform first.",
      metaDescription: "An intelligent layer over your forms that guides users and cross-validates entries against your records in real time, catching errors before submission.",
      features: [
        { name: "Intelligent Form Discovery", description: "Uses natural language queries to instantly find the exact form needed from your document repository, eliminating manual searches through websites or filing cabinets.", icon: <FaMagnifyingGlass className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Real-Time Field Validation", description: "Guides users as they type, catching errors, formatting issues, and missing information on the spot to ensure every submission is 100% complete and accurate.", icon: <FaCheckCircle className="w-8 h-8 mb-4 text-primary" />  },
        { name: "Cross-System Data Verification", description: "Automatically cross-references entries against external systems like an SIS or EHR to auto-fill data and validate information, preventing inconsistencies.", icon: <FaUserCheck className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Automated Document Filing", description: "Quickly create complete, error-free documents from templates using AI-powered autofill and smart suggestions.", icon: <RiSpeedLine className="w-8 h-8 mb-4 text-primary" /> },
      ],
      useCases: [
        { title: "Education", description: "Automate student enrollment, financial aid, and transcript processing. Eliminate common errors from paper forms and save administrative staff hours of manual work." },
        { title: "Hospitals & Clinics", description: "Streamline patient intake, consent forms, and insurance claims. Reduce costly billing errors and integrate seamlessly with EHR systems to ensure data accuracy and HIPAA compliance." },
        { title: "Government", description: "Modernize public-facing forms for permits, licenses, and services. Guide citizens through complex applications, reduce delays, and create transparent, auditable workflows." },
      ],
      demo: { type: 'video', src: '/assets/demos/formulaic.mp4', alt: 'An interactive demo of Formulaic.' },
      cta: { 
        headline: "Eliminate Bad Data. Streamline Your Workflow.", 
        buttonText: "Book a Free Assessment",
        description: "We'll assess your intake and form workflows and show where automation can improve accuracy and save time."
      },
      caseStudy: {
        href: "/assets/caseStudies/formulaic.pdf",
        filename: "Formulaic-Case-Study.pdf",
        label: "Download Case Study"
      }
    },
    "insight": {
      title: "InSight",
      tagline: "real-time event detection for private camera networks",
      description: "InSight is our event-detection system for private camera networks. It processes live video locally, raises alerts when configured trigger events occur, and does not store data unless an event is detected. That keeps your environment private while still giving your team immediate awareness when something important happens. Because detection runs on infrastructure you control rather than routing footage through a third-party cloud, InSight fits environments where continuous off-site video storage isn't acceptable — it watches for the specific conditions you define and only surfaces the moments that actually matter, instead of asking staff to monitor a wall of live feeds.",
      metaDescription: "A private, on-prem event-detection system for camera networks that alerts your team only when a trigger event occurs — nothing is stored otherwise.",
      features: [
        { name: "Trigger-Based Alerts", description: "InSight watches for configured events and notifies your team only when a trigger occurs, so you can respond quickly without constant review.", icon: <FaMagnifyingGlass className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Configurable Event Rules", description: "Define the events you care about, from entry conditions to movement thresholds, and tune detection to fit your environment.", icon: <MdOutlineScience className="w-8 h-8 mb-4 text-primary" />  },
        { name: "Local-Only Processing", description: "Video is processed inside your own secure environment to keep footage private and reduce data exposure.", icon: <FaShieldAlt className="w-8 h-8 mb-4 text-primary" /> },
        { name: "Built for Multiple Feeds", description: "Scale from a single camera to a larger event space while keeping detection responsive and operationally simple.", icon: <RiSpeedLine className="w-8 h-8 mb-4 text-primary" /> },
      ],
      useCases: [
        { title: "Event Security", description: "Detect unauthorized access, crowd surges, or other trigger conditions so staff can respond immediately during live events." },
        { title: "Venue Operations", description: "Support coordinators with real-time awareness around entrances, staging areas, and high-traffic zones without storing routine video data." },
        { title: "Safety & Incident Response", description: "Flag specific incidents or threshold events so teams can act fast and review only the moments that matter." },
      ],
      demo: { type: 'image', src: '/assets/demos/insight.jpeg', alt: 'A demo showing InSight’s real-time event detection capabilities.' },
      cta: { 
        headline: "Detect Events in Real-Time, Privately.",
        buttonText: "Book a Free Automation Assessment",
        description: "We'll assess your monitoring workflows and show where secure event detection can improve response without adding review burden."
      },
    } 
  };

  const page = id ? contentMap[id] : undefined;

  // --- Render Functions ---
  if (!page) {
    return <NotFound />;
  }

  const renderDemo = () => {
    if (!page.demo) return null;
    switch (page.demo.type) {
      case 'video': return <video src={page.demo.src} controls className="w-full rounded-lg shadow-2xl" aria-label={page.demo.alt}></video>;
      case 'image': return <img src={page.demo.src} alt={page.demo.alt} className="w-full rounded-lg shadow-2xl" />;
      case 'interactive': return <div className="w-full h-96 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-gray-400">Interactive Demo for {page.title}</div>;
      default: return null;
    }
  };

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: page.title, path: `/services/${id}` },
  ];

  // --- Main Component Return ---
  return (

    <div className="relative pt-6 pb-16">
      <Seo
        title={`${page.title} | Contextral`}
        description={page.metaDescription ?? page.description}
        jsonLd={buildServiceSchema(`/services/${id}`, page.title, page.description)}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary mb-4">
            {page.title}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {page.tagline}
          </p>
        </div>

        {/* Section 2: Overview & Demo */}
        <section className="pb-16 sm:pb-20 mx-auto">
          <div className={`grid grid-cols-1 ${page.demo ? "lg:grid-cols-2" : "text-center"} gap-12 lg:gap-16 items-center`}>
            <div className="lg:pr-8 rounded-2xl border border-borderSubtle bg-surface p-6 sm:p-8 shadow-sm">
              <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-4">Overview</h2>
              <p className="text-lg leading-relaxed text-text-secondary">{page.description}</p>

              {page.caseStudy && (
                <div className="mt-6">
                  <a
                    href={page.caseStudy.href}
                    download={page.caseStudy.filename}
                    className="inline-flex items-center gap-3 px-5 py-3 bg-primary text-text-inverse font-semibold rounded-full shadow hover:bg-primaryHover transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                    </svg>
                    {page.caseStudy.label ?? "Download Case Study"}
                  </a>
                </div>
              )}
            </div>
            { page.demo && (
              <div className="rounded-2xl border border-borderSubtle bg-surface shadow-sm">{renderDemo()}</div>
            )}
            
          </div>
        </section>

        {/* Section 3: Features */}
        <section className="py-12 sm:py-16 md:py-20 rounded-2xl border border-borderSubtle bg-surface px-6 sm:px-8 shadow-sm mb-12 sm:mb-16">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">Why Choose {page.title}?</h2>
            <p className="max-w-2xl mx-auto text-text-secondary mb-12">Here's what sets our solution apart.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-left">
              {page.features.map((feature, index) => (
                <div key={index} className="bg-surfaceSoft p-6 rounded-xl border border-borderSubtle transform hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-accentPurple/10">
                  {feature.icon}
                  <h3 className="text-xl font-bold text-text-primary mb-2">{feature.name}</h3>
                  <p className="text-text-secondary">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: Use Cases */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="container mx-auto">
            <div className="text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">Real-World Applications</h2>
                <p className="max-w-2xl mx-auto text-text-secondary mb-12">See how {page.title} can be applied to solve key challenges.</p>
            </div>
            <div className="use-cases-carousel">
              <div className="use-cases-track">
                <div className="use-cases-group" role="list" aria-label={`${page.title} use cases`}>
                  {page.useCases.map((useCase, index) => (
                    <div
                      key={`primary-${useCase.title}-${index}`}
                      role="listitem"
                      className="w-[300px] sm:w-[340px] md:w-[380px] flex-shrink-0 bg-surface p-6 sm:p-8 rounded-xl border border-borderSubtle shadow-sm hover:shadow-lg hover:shadow-accentPurple/10 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold mb-3 text-primary">{useCase.title}</h3>
                      <p className="text-text-secondary">{useCase.description}</p>
                    </div>
                  ))}
                </div>

                <div className="use-cases-group" aria-hidden="true">
                  {page.useCases.map((useCase, index) => (
                    <div
                      key={`duplicate-${useCase.title}-${index}`}
                      className="w-[300px] sm:w-[340px] md:w-[380px] flex-shrink-0 bg-surface p-6 sm:p-8 rounded-xl border border-borderSubtle shadow-sm hover:shadow-lg hover:shadow-accentPurple/10 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold mb-3 text-primary">{useCase.title}</h3>
                      <p className="text-text-secondary">{useCase.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Section 5: Call to Action */}
        <section className="pb-8 sm:pb-12">
          <div className="container mx-auto">
            <div className="relative overflow-hidden bg-gradient-to-r from-primary via-primaryHover to-accentPurple text-center rounded-2xl p-8 sm:p-10 md:p-12 shadow-xl shadow-primary/25">
              <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
              <h2 className="relative text-2xl sm:text-3xl md:text-4xl font-bold text-text-inverse mb-4">{page.cta.headline}</h2>
              {page.cta.description && (
                <p className="relative max-w-3xl mx-auto text-base sm:text-lg text-surfaceSoft mb-8">
                  {page.cta.description}
                </p>
              )}
              <button
                type="button"
                onClick={() => page.cta.link ? (window.location.href = page.cta.link) : bookAssessment()}
                className="group relative w-full sm:w-auto inline-flex items-center justify-center bg-surface text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg"
              >
                {page.cta.buttonText}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceDetail;
